import React, { useState } from "react";
import { AppHeader } from "./CommunitySelection";
import { submitContribution } from "../lib/api";
import { useLang } from "../lib/i18n";
import { ArrowLeft, Send, CheckCircle2, ClipboardList } from "lucide-react";

const AREAS = [
  { key: "housing", label: "Housing & rent", hint: "Affordability, availability, cost burden" },
  { key: "transportation", label: "Transportation", hint: "Public transit, car dependence, distances" },
  { key: "internet", label: "Internet access", hint: "Broadband availability and reliability" },
  { key: "healthcare", label: "Healthcare access", hint: "Clinics, hospitals, insurance coverage" },
  { key: "jobs", label: "Jobs & employment", hint: "Available work, wages, stability" },
  { key: "childcare", label: "Childcare", hint: "Availability, affordability, waitlists" },
  { key: "weather", label: "Weather / environmental risk", hint: "Storms, heat, flooding, other hazards" },
];

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="text-sm font-semibold text-ink">
      {label} {required && <span className="text-[#8E2929]">*</span>}
    </label>
    {hint && <p className="text-xs text-ink-soft mt-0.5">{hint}</p>}
    <div className="mt-1.5">{children}</div>
  </div>
);

const inputCls =
  "w-full rounded-lg border border-[var(--line)] px-4 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin";
const areaCls =
  "w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin resize-none";

export const ContributeCommunity = ({ onBack, onHome }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "", conference: "", district_council: "", region: "", email: "",
    community_name: "", description: "",
    housing: "", transportation: "", internet: "", healthcare: "", jobs: "", childcare: "", weather: "",
    biggest_barriers: "", data_source: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const required = ["name", "conference", "district_council", "region", "email", "community_name", "description", "biggest_barriers"];
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canSubmit = required.every((k) => form[k].trim()) && emailOk;

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await submitContribution(form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : t("Please check the required fields and try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen">
        <AppHeader subtitle={t("Contribute your community")} />
        <div className="max-w-2xl mx-auto px-5 sm:px-6 py-16">
          <div className="bg-vin-dark rounded-lg p-8 sm:p-10 text-center animate-rise">
            <CheckCircle2 className="w-10 h-10 text-white/90 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-3xl font-semibold text-white">{t("Thank you,")} {form.name.split(" ")[0]}.</h2>
            <p className="mt-4 text-white/85 leading-relaxed">
              {t("Your observations about")} <span className="font-semibold">{form.community_name}</span> {t("have been shared with the Root Causes Challenge team. Contributions like yours are the first step toward uncovering the unique root causes of poverty in every community.")}
            </p>
            <button
              data-testid="contribute-home-button"
              onClick={onHome}
              className="mt-8 inline-flex items-center gap-2 bg-white text-vin font-semibold px-7 py-3.5 rounded-full hover:bg-vin hover:text-white transition-colors duration-200"
            >
              {t("Return to start")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-10">
      <AppHeader subtitle={t("Contribute your community")} />
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-8 sm:py-10">
        <button
          data-testid="contribute-back-button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-vin transition-colors duration-200 mb-6"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> {t("Back")}
        </button>

        <div className="animate-rise">
          <div className="inline-flex items-center gap-2 text-vin bg-vin/5 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] font-semibold">
            <ClipboardList className="w-3.5 h-3.5" strokeWidth={2} /> {t("Look at your own community")}
          </div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {t("Contribute your community")}
          </h2>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            {t("Using what you know and publicly available data, tell us about the barriers families face in your community. Your submission helps build future, community-specific insights. Contact details are required so we can follow up and keep the data trustworthy.")}
          </p>
        </div>

        <div className="mt-8 space-y-8">
          {/* Contributor */}
          <section className="bg-white rounded-lg border border-[var(--line)] p-6 space-y-4">
            <h3 className="font-display text-lg font-semibold text-ink">{t("About you")}</h3>
            <Field label={t("Full name")} required>
              <input data-testid="contrib-name" className={inputCls} value={form.name} onChange={set("name")} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("Organization")} required>
                <input data-testid="contrib-conference" className={inputCls} value={form.conference} onChange={set("conference")} />
              </Field>
              <Field label={t("Team or chapter")} required>
                <input data-testid="contrib-district" className={inputCls} value={form.district_council} onChange={set("district_council")} />
              </Field>
              <Field label={t("Region")} required>
                <input data-testid="contrib-region" className={inputCls} value={form.region} onChange={set("region")} />
              </Field>
              <Field label={t("Contact email")} required>
                <input data-testid="contrib-email" type="email" className={inputCls} value={form.email} onChange={set("email")} />
              </Field>
            </div>
          </section>

          {/* Community */}
          <section className="bg-white rounded-lg border border-[var(--line)] p-6 space-y-4">
            <h3 className="font-display text-lg font-semibold text-ink">{t("Your community")}</h3>
            <Field label={t("Community name & state")} required>
              <input data-testid="contrib-community-name" className={inputCls} value={form.community_name} onChange={set("community_name")} placeholder={t("e.g. Springfield, Missouri")} />
            </Field>
            <Field label={t("One-sentence description")} required>
              <input data-testid="contrib-description" className={inputCls} value={form.description} onChange={set("description")} placeholder={t("Describe your community in a sentence")} />
            </Field>
          </section>

          {/* Barriers by area */}
          <section className="bg-white rounded-lg border border-[var(--line)] p-6 space-y-4">
            <div>
              <h3 className="font-display text-lg font-semibold text-ink">{t("Barriers you observe")}</h3>
              <p className="text-sm text-ink-soft mt-1">{t("A short note for each area (optional, but valuable).")}</p>
            </div>
            {AREAS.map((a) => (
              <Field key={a.key} label={t(a.label)} hint={t(a.hint)}>
                <textarea
                  data-testid={`contrib-${a.key}`}
                  rows={2}
                  className={areaCls}
                  value={form[a.key]}
                  onChange={set(a.key)}
                />
              </Field>
            ))}
          </section>

          {/* Root causes + source */}
          <section className="bg-white rounded-lg border border-[var(--line)] p-6 space-y-4">
            <Field label={t("The biggest root-cause barriers I see in my community")} required>
              <textarea data-testid="contrib-barriers" rows={4} className={areaCls} value={form.biggest_barriers} onChange={set("biggest_barriers")} />
            </Field>
            <Field label={t("A public data source I used")} hint={t("Optional — a link or the name of a report/dataset")}>
              <input data-testid="contrib-source" className={inputCls} value={form.data_source} onChange={set("data_source")} placeholder={t("e.g. U.S. Census / local report")} />
            </Field>
          </section>

          {error && <p data-testid="contrib-error" className="text-sm text-[#8E2929]">{error}</p>}

          <button
            data-testid="contrib-submit-button"
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-vin text-white font-semibold px-8 py-4 rounded-full hover:bg-vin-hover transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" strokeWidth={1.75} />
            {submitting ? t("Submitting…") : t("Submit to the committee")}
          </button>
          {!canSubmit && (
            <p className="text-xs text-ink-soft">{t("All fields marked with * are required, including a valid email.")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContributeCommunity;
